/**
 * Push-notification integration config form domain — decides which auth type
 * an integration dialog is operating under and what a save actually sends.
 * An integration's parent record may pin the auth type (customer cannot
 * change it); otherwise the customer picks one per CustomerIntegration and
 * may switch it at any time, including to Basic (key 0).
 *
 * Contains:
 * - resolveAuthTypeKey — effective auth-type key for the dialog (parent pin
 *   wins, then the user's in-dialog selection, then the saved value)
 * - isAuthTypeLocked — whether the parent integration pins the auth type
 * - buildIntegrationConfigPatch — changed-credential-fields + auth_type PATCH
 *   body builder
 *
 * Returns plain JS — no JSX, no redux.
 */

/**
 * @param {?number} parentAuthType   - auth type pinned by the parent Integration, or null.
 * @param {?number} savedAuthType    - auth type stored on the CustomerIntegration, or null.
 * @param {?number} selectedAuthType - the user's in-dialog selection, or null if untouched.
 * @returns {?number} The auth-type key the dialog operates under. Keys are
 *   0-based, so all comparisons are null-safe (Basic = 0 is a valid key).
 */
export function resolveAuthTypeKey({ parentAuthType, savedAuthType, selectedAuthType }) {
  return parentAuthType ?? selectedAuthType ?? savedAuthType ?? null;
}

/**
 * @param {?number} parentAuthType - auth type pinned by the parent Integration, or null.
 * @returns {boolean} True when the parent Integration dictates the auth type.
 */
export function isAuthTypeLocked(parentAuthType) {
  return parentAuthType != null;
}

/**
 * @param {Object<string, string>} form     - current credential field values.
 * @param {Object<string, string>} original - field values captured at dialog open.
 * @param {?number} parentAuthType          - auth type pinned by the parent Integration, or null.
 * @param {?number} savedAuthType           - auth type stored on the CustomerIntegration, or null.
 * @param {?number} selectedAuthType        - the user's in-dialog selection, or null if untouched.
 * @returns {Object} PATCH body: only credential fields that changed, plus
 *   auth_type when the auth type is not parent-pinned and the user picked a
 *   value different from the saved one (Basic = 0 included).
 */
export function buildIntegrationConfigPatch({ form, original, parentAuthType, savedAuthType, selectedAuthType }) {
  const patch = {};
  Object.keys(form).forEach(key => {
    if (form[key] !== original[key]) {
      patch[key] = form[key];
    }
  });
  if (parentAuthType == null && selectedAuthType != null && selectedAuthType !== savedAuthType) {
    patch.auth_type = selectedAuthType;
  }
  return patch;
}
